import { Command } from 'cmdk';

import { useCustomerGroupsPagination } from '../../../../admin/customergroup/hooks/useCustomerGroupsPagination';
import { useCustomerList } from '../../../../group/customer/detail/hooks/useCustomerList';
import { formatCompanyName } from '../../../helpers/formatCompanyName.helper';
import { translate } from '../../../helpers/translate.helper';
import { useDebounce } from '../../../hooks/useDebounce';
import { GroupContextType } from '../../../utils/groupContextType';
import { useAutoSelectFirstCommandItem } from '../../hooks/useAutoSelectFirstCommandItem';
import { useGroupContextSelectionHandler } from '../../hooks/useGroupContextSelectionHandler';
import { useShortcutMenu } from '../../hooks/useShortcutMenu';
import { CommandEmptyState } from '../CommandEmptyState';
import { CommandErrorState } from '../CommandErrorState';
import { CommandItem } from '../CommandItem';
import { CommandSkeletonList } from '../CommandSkeletonList';

import { GroupContextCustomerGroupDetail } from './GroupContextCustomerGroupDetail';

export const CustomerGroupSelection = () => {
    const { search } = useShortcutMenu();
    const debouncedSearch = useDebounce(search, 300);
    const customerGroupsQuery = useCustomerGroupsPagination(debouncedSearch, 0);
    const customersQuery = useCustomerList({ searchQuery: debouncedSearch });
    const { selectCompany } = useGroupContextSelectionHandler();

    const firstItemValue = customerGroupsQuery.data?.content[0]
        ? `group-${customerGroupsQuery.data.content[0].id}`
        : customersQuery.data?.[0]
          ? `customer-${customersQuery.data[0].id}`
          : undefined;

    useAutoSelectFirstCommandItem(firstItemValue);

    if (customerGroupsQuery.isPending || customersQuery.isPending) {
        return <CommandSkeletonList />;
    }

    if (customerGroupsQuery.isError || customersQuery.isError) {
        return <CommandErrorState />;
    }

    const hasGroups = (customerGroupsQuery.data?.content.length ?? 0) > 0;
    const hasCompanies = (customersQuery.data?.length ?? 0) > 0;

    if (!hasGroups && !hasCompanies) {
        return <CommandEmptyState />;
    }

    return (
        <Command.List>
            <Command.Group heading={translate('shortcutMenu.groups')}>
                {customerGroupsQuery.data?.content.map((group) => (
                    <GroupContextCustomerGroupDetail key={group.id} customerGroup={group} />
                ))}
            </Command.Group>
            <Command.Group heading={translate('shortcutMenu.companies')}>
                {customersQuery.data?.map((customer) => (
                    <CommandItem
                        key={customer.id}
                        value={`customer-${customer.id}`}
                        onSelect={() => {
                            void selectCompany({
                                companyId: customer.id,
                                groupId: customer.customerGroupId,
                                type: GroupContextType.CUSTOMER_COMPANY,
                            });
                        }}
                    >
                        {formatCompanyName({ company: customer })}
                    </CommandItem>
                ))}
            </Command.Group>
        </Command.List>
    );
};
